import { useState, useEffect } from 'react';
import { motion, PanInfo, useMotionValue, useTransform } from 'framer-motion';
import { api } from '../lib/api';
import { Edit, Star, StarOff, ChevronUp } from 'lucide-react';
import { useLocation } from 'wouter';
import BottomNav from '../components/BottomNav';

interface Card {
  id: number;
  title: string;
  content: string;
  card_type: string;
  insight: string;
  source_quote: string;
  book_id: number;
}

export default function Cards() {
  const [, setLocation] = useLocation();
  const [cards, setCards] = useState<Card[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    loadCards();
  }, []);

  const loadCards = async () => {
    try {
      const response = await api.getCards();
      if (response.success && response.data) {
        setCards(response.data);
      }
    } catch (error) {
      console.error('Failed to load cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwipe = (direction: 'up' | 'down') => {
    if (direction === 'up' && currentIndex < cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFavorite(false);
    } else if (direction === 'down' && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFavorite(false);
    }
  };

  const toggleFavorite = async () => {
    if (cards[currentIndex]) {
      try {
        await api.addFavorite(cards[currentIndex].id);
        setIsFavorite(!isFavorite);
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">加载中...</p>
        </div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center px-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">还没有知识卡片</h2>
          <p className="text-text-secondary mb-6">上传一本书籍开始学习吧</p>
          <button
            onClick={() => setLocation('/upload')}
            className="px-8 py-3 bg-gradient-accent rounded-xl text-white font-medium shadow-glow"
          >
            上传书籍
          </button>
        </div>
      </div>
    );
  }

  const currentCard = cards[currentIndex];

  return (
    <div className="fixed inset-0 gradient-bg overflow-hidden">
      {/* Full-screen Card Container - No Header */}
      <div className="absolute inset-0 pb-20">
        <SwipeCard
          card={currentCard}
          onSwipe={handleSwipe}
          index={currentIndex}
          total={cards.length}
        />
      </div>

      {/* Action Buttons - Bottom Right Corner */}
      <div className="fixed bottom-24 right-6 z-30 flex flex-col gap-3">
        <button
          onClick={() => {/* Open note editor */}}
          className="w-12 h-12 glass-strong rounded-2xl flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        >
          <Edit size={20} className="text-primary" />
        </button>
        
        <button
          onClick={toggleFavorite}
          className="w-12 h-12 glass-strong rounded-2xl flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
        >
          {isFavorite ? (
            <Star size={20} className="text-yellow-400" fill="currentColor" />
          ) : (
            <StarOff size={20} className="text-text-secondary" />
          )}
        </button>
      </div>

      <BottomNav />
    </div>
  );
}

// Swipe Card Component with Vertical Scrolling
function SwipeCard({
  card,
  onSwipe,
  index,
  total
}: {
  card: Card;
  onSwipe: (direction: 'up' | 'down') => void;
  index: number;
  total: number;
}) {
  const y = useMotionValue(0);
  const opacity = useTransform(y, [-300, -150, 0, 150, 300], [0, 1, 1, 1, 0]);

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // Swipe up to next card
    if (info.offset.y < -100 && index < total - 1) {
      onSwipe('up');
    }
    // Swipe down to previous card
    else if (info.offset.y > 100 && index > 0) {
      onSwipe('down');
    }
  };

  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center px-6 cursor-grab active:cursor-grabbing"
      style={{ y, opacity }}
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      onDragEnd={handleDragEnd}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="w-full max-w-2xl">
        {/* Progress Indicator */}
        <div className="text-center text-sm text-text-secondary/60 mb-8">
          {index + 1} / {total}
        </div>

        {/* Card Content */}
        <div className="space-y-8 text-center">
          {/* Title */}
          <h2 className="text-3xl md:text-4xl font-bold text-gradient leading-tight px-4">
            {card.title}
          </h2>

          {/* Content */}
          <div className="prose prose-invert max-w-none">
            <p className="text-lg md:text-xl leading-relaxed text-text-primary px-4">
              {card.content}
            </p>
          </div>

          {/* Insight */}
          {card.insight && (
            <div className="glass rounded-2xl p-6 border-l-4 border-primary text-left max-w-xl mx-auto">
              <h3 className="text-sm font-semibold text-primary mb-3">💡 实践启示</h3>
              <p className="text-base text-text-secondary leading-relaxed">{card.insight}</p>
            </div>
          )}

          {/* Source Quote */}
          {card.source_quote && (
            <div className="glass rounded-2xl p-6 text-left max-w-xl mx-auto">
              <h3 className="text-xs font-semibold text-text-secondary/70 mb-3">📖 原文摘录</h3>
              <p className="text-sm text-text-secondary/90 italic leading-relaxed">"{card.source_quote}"</p>
            </div>
          )}
        </div>

        {/* Swipe Hint - Bottom Center */}
        <div className="text-center pt-12 pb-8">
          <div className="inline-flex flex-col items-center gap-2 text-sm text-text-secondary/60">
            <ChevronUp className="w-6 h-6 animate-bounce" />
            <span>上滑查看下一张</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
